<template>
  <div>
    <!-- Loader displayed when isLoading is true -->
    <div
      class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"
    ></div>
  </div>
  <div class="space-y-6">
    <div
      v-if="billDetails.patient?.patient_insurance_info?.insurance"
      class="grid grid-cols-3 w-full"
    >
      <div
        class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"
      ></div>
    </div>

    <div class="space-y-6">
      <!-- Insurance info row -->
      <div
        v-if="billDetails.patient?.patient_insurance_info?.insurance"
        class="grid grid-cols-3 flex w-full"
      >
        <div>
          <p class="text-sm font-semibold text-gray-600">Insurance Name:</p>
          <p class="text-sm">
            {{ billDetails.patient?.patient_insurance_info?.insurance?.ProviderName }}
          </p>
        </div>
        <div>
          <p class="text-sm font-semibold text-gray-600">Member Number:</p>
          <p class="text-sm">
            {{ billDetails.patient?.patient_insurance_info?.MemberNumber }}
          </p>
        </div>
        <div>
          <p class="text-sm font-semibold text-gray-600">Total Amount:</p>
          <p class="text-sm">{{ formatCurrency(billDetails.total_amount) }}</p>
        </div>
      </div>

      <!-- Balance row for non-insurance -->
      <div>
        <div
          v-if="paymentDetails.paymentMethod !== 'Insurance'"
          class="grid grid-cols-1 flex w-full"
        >
          <p class="text-sm font-semibold text-gray-600">Balance:</p>
          <p class="text-sm">{{ formatCurrency(billDetails.balance) }}</p>
        </div>
      </div>

      <!-- ─────────────────────────────────────────────────────────────
           DISCOUNT SECTION
           Only shown for non-insurance bills OR insurance bills with a copay.
           For a pure FFS bill (no copay) there is nothing to discount.
      ──────────────────────────────────────────────────────────────── -->
      <div
        v-if="!isInsuranceScheme || (isInsuranceScheme && copayAmount > 0)"
        class="border border-dashed border-yellow-400 rounded-md p-4 bg-yellow-50"
      >
        <div class="flex items-center justify-between mb-3">
          <p class="text-sm font-semibold text-gray-700">Apply Discount</p>
          <!-- Toggle between percentage and fixed amount -->
          <div class="flex items-center space-x-2">
            <button
              :class="[
                'px-3 py-1 text-xs rounded',
                discountType === 'percentage'
                  ? 'bg-custom-yellow text-white'
                  : 'bg-gray-200 text-gray-700',
              ]"
              @click="setDiscountType('percentage')"
            >
              % Percentage
            </button>
            <button
              :class="[
                'px-3 py-1 text-xs rounded',
                discountType === 'fixed'
                  ? 'bg-custom-yellow text-white'
                  : 'bg-gray-200 text-gray-700',
              ]"
              @click="setDiscountType('fixed')"
            >
              KES Fixed
            </button>
          </div>
        </div>

        <div class="grid grid-cols-3 gap-4 items-end">
          <!-- Discount input -->
          <div>
            <label class="text-xs text-gray-500 mb-1 block">
              {{ discountType === 'percentage' ? 'Discount (%)' : 'Discount Amount (KES)' }}
            </label>
            <div class="relative">
              <span
                v-if="discountType === 'percentage'"
                class="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm"
              >%</span>
              <input
                v-model.number="discountInput"
                type="number"
                min="0"
                :max="discountType === 'percentage' ? 100 : effectiveBillBalance"
                step="0.01"
                @input="onDiscountInputChange"
                :class="[
                  'mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50',
                  'focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50',
                  discountType === 'percentage' ? 'pl-6' : 'pl-2',
                ]"
                placeholder="0"
              />
            </div>
          </div>

          <!-- Computed discount amount display -->
          <div>
            <p class="text-xs text-gray-500 mb-1">Discount Amount</p>
            <p class="text-sm font-semibold text-red-600">
              - {{ formatCurrency(computedDiscountAmount) }}
            </p>
          </div>

          <!-- Effective amount payable -->
          <div>
            <p class="text-xs text-gray-500 mb-1">Amount Payable After Discount</p>
            <p class="text-sm font-semibold text-green-700">
              {{ formatCurrency(effectivePayableAmount) }}
            </p>
          </div>
        </div>

        <!-- Discount validation warning -->
        <p v-if="discountValidationError" class="text-xs text-red-500 mt-2">
          {{ discountValidationError }}
        </p>
      </div>

      <!-- ─────────────────────────────────────────────────────────────
           PAYMENT ROWS
      ──────────────────────────────────────────────────────────────── -->
      <div class="grid grid-cols-1 gap-4">
        <div v-if="!isInsuranceScheme">
          <div class="grid grid-cols-5 px-3 flex w-full">
            <div>
              <p class="text-sm font-semibold text-gray-900">Payment Method</p>
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-900">Transaction Code</p>
            </div>
            <div>
              <p class="text-sm font-semibold ml-10 text-gray-900">Amount Paid</p>
            </div>
            <div>
              <p class="text-sm font-semibold ml-10 text-gray-900">Balance</p>
            </div>
            <div>
              <button
                class="px-8 py-1 bg-custom-yellow text-white rounded"
                @click="addPayment"
              >
                +
              </button>
            </div>
          </div>
          <div
            v-for="(payment, index) in paymentDetailsList"
            :key="index"
            class="grid grid-cols-5 mt-4 flex w-full"
          >
            <div class="flex flex-col px-3">
              <select
                :disabled="state === 'view'"
                v-model="payment.payment_method_id"
                @change="checkPaymentMethod(index)"
                class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
              >
                <option value="" disabled>Select Payment Method</option>
                <!--
                  cashierPaymentMethods excludes FFS (ST-003).
                  FFS is a post-payment method settled programmatically —
                  cashiers should never manually select it at the payment window.
                -->
                <option
                  v-for="method in cashierPaymentMethods"
                  :key="method.id"
                  :value="method"
                >
                  {{ method.name }}
                </option>
              </select>
            </div>
            <div class="flex flex-col px-8">
              <!--
                Transaction code is only required for Mpesa (ST-001).
                Cash (ST-002) and Card (ST-004) do not need a reference number.
              -->
              <input
                v-if="payment.payment_method_id?.code === 'ST-001'"
                type="text"
                v-model="payment.transaction_code"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
                placeholder="Transaction Code"
                :required="payment.payment_method_id?.code === 'ST-001'"
              />
              <input
                v-else
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 bg-gray-200 cursor-not-allowed"
                disabled
                placeholder="N/A"
              />
            </div>
            <div class="flex flex-col px-8">
              <input
                type="number"
                v-model.number="payment.amount_paid"
                @keyup="updateBalance(index)"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
                placeholder="Amount"
                required
              />
            </div>
            <div>
              <p class="text-sm font-semibold ml-10 text-gray-900">
                {{ formatCurrency(payment.balance) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Insurance scheme payment rows -->
        <div v-else>
          <div v-if="copayAmount > 0">
            <div class="bg bg-blue-200 text-black p-4">
              <p>This insurance bill requires a copay amount of {{ formatCurrency(copayAmount) }}</p>
            </div>
            <div class="grid grid-cols-5 px-3 flex w-full mt-2">
              <div>
                <p class="text-md text-center font-semibold text-gray-900">Payment Method</p>
              </div>
              <div>
                <p class="text-md text-center font-semibold text-gray-900">Transaction Code</p>
              </div>
              <div>
                <p class="text-md text-center font-semibold ml-10 text-gray-900">Amount Paid</p>
              </div>
              <div>
                <p class="text-md text-center font-semibold ml-10 text-gray-900">Balance</p>
              </div>
              <div>
                <button
                  class="px-8 py-1 bg-custom-yellow text-white rounded"
                  @click="addCopayRow"
                >
                  +
                </button>
              </div>
            </div>
            <div
              v-for="(payment, index) in paymentDetailsList"
              :key="index"
              class="grid grid-cols-5 mt-4 flex w-full"
            >
              <template v-if="!payment.editable">
                <div class="flex text-center flex-col px-3">Insurance</div>
                <div class="flex text-center flex-col px-3">N/A</div>
                <div class="flex text-center flex-col px-3">
                  {{ formatCurrency(billDetails.total_amount - copayAmount) }}
                </div>
                <div class="flex text-center flex-col px-3">
                  {{ formatCurrency(copayAmount) }}
                </div>
              </template>
              <template v-else>
                <div class="flex flex-col px-3">
                  <select
                    :disabled="state === 'view'"
                    v-model="payment.payment_method_id"
                    @change="checkPaymentMethod(index)"
                    class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
                  >
                    <option value="" disabled>Select Payment Method</option>
                    <!--
                      Also exclude FFS from copay rows — same reasoning applies.
                      Copay is paid in cash, mpesa, or card, never FFS.
                    -->
                    <option
                      v-for="method in cashierPaymentMethods"
                      :key="method.id"
                      :value="method"
                    >
                      {{ method.name }}
                    </option>
                  </select>
                </div>
                <div class="flex flex-col px-8">
                  <input
                    v-if="payment.payment_method_id?.code === 'ST-001'"
                    type="text"
                    v-model="payment.transaction_code"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
                    placeholder="Transaction Code"
                    :required="payment.payment_method_id?.code === 'ST-001'"
                  />
                  <input
                    v-else
                    type="text"
                    class="mt-1 block w-full rounded-md border-gray-300 bg-gray-200 cursor-not-allowed"
                    disabled
                    placeholder="N/A"
                  />
                </div>
                <div class="flex flex-col px-8">
                  <input
                    type="number"
                    v-model.number="payment.amount_paid"
                    @keyup="updateBalance(index)"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
                    placeholder="Amount"
                    required
                  />
                </div>
                <div>
                  <p class="text-sm font-semibold ml-10 text-gray-900">
                    {{ formatCurrency(payment.balance) }}
                  </p>
                </div>
              </template>
            </div>
          </div>
          <div v-else>
            <div class="bg bg-green-200 text-black p-4">
              <p>
                This is an FFS insurance bill and therefore requires no payment, click 'Close Bill'
                button to close this patient bill.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- SHA Claim Preview Section -->
      <div v-if="shaClaimPreview" class="mt-4 p-4 border border-orange-200 rounded-lg bg-orange-50">
        <h4 class="font-semibold text-orange-800 mb-2">SHA Claim Preview</h4>
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p class="text-gray-600">Visit Number:</p>
            <p class="font-medium">{{ shaVisitNumber }}</p>
          </div>
          <div>
            <p class="text-gray-600">Claim Status:</p>
            <p class="font-medium">{{ shaClaimStatus || 'Pending' }}</p>
          </div>
          <div>
            <p class="text-gray-600">Total Billed:</p>
            <p class="font-medium">{{ formatCurrency(shaClaimPreview.raw_response?.totalAmount || shaClaimPreview.raw_response?.total_amount || 0) }}</p>
          </div>
          <div>
            <p class="text-gray-600">Amount Covered (SHA):</p>
            <p class="font-medium text-green-600">{{ formatCurrency(shaClaimPreview.total_reimbursement || 0) }}</p>
          </div>
          <div v-if="shaClaimPreview.patient_copay" class="col-span-2">
            <p class="text-gray-600">Patient Copay:</p>
            <p class="font-medium text-orange-600">{{ formatCurrency(shaClaimPreview.patient_copay) }}</p>
          </div>
          <div v-if="shaClaimPreview.preauth_issues && shaClaimPreview.preauth_issues.length" class="col-span-2">
            <p class="text-gray-600">Preauth Issues:</p>
            <ul class="list-disc list-inside text-orange-700">
              <li v-for="(issue, idx) in shaClaimPreview.preauth_issues" :key="idx">{{ issue }}</li>
            </ul>
          </div>
        </div>
        <div v-if="shaClaimPreview.interventions && shaClaimPreview.interventions.length" class="mt-3">
          <table class="w-full text-xs border border-gray-200">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-2 py-1 text-left">Code</th>
                <th class="px-2 py-1 text-left">Service</th>
                <th class="px-2 py-1 text-right">Amount</th>
                <th class="px-2 py-1 text-right">Patient Pay</th>
                <th class="px-2 py-1 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in shaClaimPreview.interventions" :key="idx">
                <td class="px-2 py-1 border-t">{{ item.intervention_code || item.code || item.interventionCode || '-' }}</td>
                <td class="px-2 py-1 border-t">{{ item.name || item.description || item.intervention_name || '-' }}</td>
                <td class="px-2 py-1 border-t text-right">{{ formatCurrency(item.amount || item.tariff_amount || item.total_amount || 0) }}</td>
                <td class="px-2 py-1 border-t text-right">{{ formatCurrency(item.patient_payable || item.patientPayable || item.copay || 0) }}</td>
                <td class="px-2 py-1 border-t">{{ item.status || 'Active' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex justify-end space-x-4 p-4 border-t mt-5">
        <button
          @click="resetForm"
          class="px-8 py-1 bg-gray-200 text-gray-800 rounded"
        >
          Cancel
        </button>
        <button
          @click="showConfirmModal"
          class="px-8 py-1 bg-custom-yellow text-white rounded"
        >
          {{
            !isInsuranceScheme
              ? 'Confirm Payment'
              : copayAmount > 0
              ? 'Confirm Copay Payment'
              : 'Close Bill'
          }}
        </button>
      </div>

      <!-- ─────────────────────────────────────────────────────────────
           CONFIRMATION MODAL
      ──────────────────────────────────────────────────────────────── -->
      <div
        v-if="showModal"
        class="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center"
      >
        <div class="bg-white p-6 rounded shadow-lg w-3/4 max-w-3xl">
          <h3 class="text-lg text-center font-semibold">Confirm Payment</h3>

          <div
            v-if="!isInsuranceScheme || (isInsuranceScheme && copayAmount > 0)"
            class="mt-4"
          >
            <!-- Discount summary in the confirmation modal -->
            <div
              v-if="computedDiscountAmount > 0"
              class="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded"
            >
              <div class="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <span class="text-gray-500">Original Amount:</span>
                  <p class="font-semibold">{{ formatCurrency(effectiveBillBalance) }}</p>
                </div>
                <div>
                  <span class="text-gray-500">Discount Applied:</span>
                  <p class="font-semibold text-red-600">
                    - {{ formatCurrency(computedDiscountAmount) }}
                    <span v-if="discountType === 'percentage'" class="text-xs text-gray-400">
                      ({{ discountInput }}%)
                    </span>
                  </p>
                </div>
                <div>
                  <span class="text-gray-500">Amount Payable:</span>
                  <p class="font-semibold text-green-700">
                    {{ formatCurrency(effectivePayableAmount) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Payment rows in modal -->
            <div
              v-for="(payment, index) in paymentDetailsList"
              :key="index"
              class="grid grid-cols-4 gap-4 mt-4"
            >
              <div class="flex items-center justify-center">
                <p>{{ payment.payment_method_id?.name || 'Insurance' }}</p>
              </div>
              <div class="flex items-center justify-center">
                <p>{{ payment.transaction_code || 'N/A' }}</p>
              </div>
              <div class="flex items-center justify-center">
                <p>{{ formatCurrency(payment.amount_paid) }}</p>
              </div>
              <div class="flex items-center justify-center">
                <input
                  type="checkbox"
                  v-model="payment.selected"
                  class="form-checkbox"
                />
              </div>
            </div>
          </div>
          <div v-else>
            <p class="text-sm text-gray-600">
              This is an FFS insurance bill and therefore requires no payment, click 'Confirm' button
              to close this patient bill.
            </p>
          </div>

          <div class="flex justify-end mt-4 border-t">
            <button
              @click="closeModal"
              class="px-8 py-1 bg-gray-200 mt-4 text-gray-800 rounded"
            >
              Cancel
            </button>
            <button
              @click="confirmPaymentTransactions"
              class="px-8 py-1 bg-custom-yellow ml-4 mt-4 text-white rounded"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";

export default {
  name: "PaymentInformation",
  props: {
    state: {
      type: String,
      required: true,
    },
    visitId: {
      type: String,
      required: true,
    },
    billDetails: {
      type: Object,
      required: true,
    },
    paymentMethods: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isCoveringFullAmount: false,
      showModal: false,
      paymentDetails: {
        totalAmount: 0,
        paymentMethod: "",
      },
      localBillDetails: this.billDetails,
      startingBalance: 0,
      paymentDetailsList: [
        {
          payment_method_id: null,
          transaction_code: "",
          selected: true,
          amount_paid: 0,
          balance: 0,
          editable: true,
        },
      ],
      // ── Discount state ──────────────────────────────────────────────
      // 'percentage' | 'fixed'
      discountType: "percentage",
      // The raw value the cashier types in (either a % number or a KES amount)
      discountInput: 0,
      shaClaimPreview: null,
      shaVisitNumber: null,
      shaClaimStatus: null,
    };
  },
  computed: {
    // ── Cashier-visible payment methods ───────────────────────────────
    //
    // FFS (ST-003) is a post-payment method: the FFS company is billed
    // programmatically via beneficiary records, not collected at the cashier
    // window. Removing it here prevents cashiers from accidentally selecting
    // it as if it were a cash-equivalent method.
    //
    // This is a display filter only — the parent still passes all methods,
    // and the FFS billing path is unaffected.
    cashierPaymentMethods() {
      return this.paymentMethods.filter(m => m.code !== 'ST-003');
    },

    isPaymentMethodInsurance() {
      return this.billDetails.patient?.patient_insurance_info?.InsuranceId !== "";
    },
    isInsuranceScheme() {
      return (
        this.billDetails.patient_scheme?.scheme?.scheme_type === 1 ||
        this.billDetails.patient_scheme?.scheme?.scheme_type === 2
      );
    },
    copayAmount() {
      if (!this.billDetails.patient_scheme) return 0;
      if (this.billDetails.patient_scheme.scheme.copay_type === 1) {
        if (this.billDetails.patient_scheme.scheme.copay_amount) {
          return (
            (this.billDetails.total_amount *
              this.billDetails.patient_scheme.scheme.copay_amount) /
            100
          );
        }
      } else if (this.billDetails.patient_scheme.scheme.copay_type === 2) {
        return this.billDetails.patient_scheme.scheme.copay_amount;
      }
      return 0;
    },

    // ── Discount computeds ─────────────────────────────────────────────

    /**
     * The balance that the discount should be applied against.
     * For insurance+copay bills this is just the copay portion, not the full bill.
     * For regular bills this is the full outstanding balance.
     */
    effectiveBillBalance() {
      if (this.isInsuranceScheme && this.copayAmount > 0) {
        return parseFloat(this.copayAmount) || 0;
      }
      return parseFloat(this.billDetails.balance) || 0;
    },

    /**
     * The absolute KES value of the discount, regardless of input type.
     * Always clamped to [0, effectiveBillBalance] so we never discount more than is owed.
     */
    computedDiscountAmount() {
      if (!this.discountInput || this.discountInput <= 0) return 0;
      let amount = 0;
      if (this.discountType === "percentage") {
        // Cap at 100%
        const pct = Math.min(parseFloat(this.discountInput) || 0, 100);
        amount = (this.effectiveBillBalance * pct) / 100;
      } else {
        amount = parseFloat(this.discountInput) || 0;
      }
      // Never allow a discount larger than what the patient owes
      return Math.min(amount, this.effectiveBillBalance);
    },

    /**
     * What the patient actually needs to pay in cash/mpesa/card/insurance after discount.
     */
    effectivePayableAmount() {
      return Math.max(this.effectiveBillBalance - this.computedDiscountAmount, 0);
    },

    /**
     * Validation message shown inline in the discount section.
     */
    discountValidationError() {
      if (this.discountType === "percentage") {
        if (this.discountInput < 0) return "Discount percentage cannot be negative.";
        if (this.discountInput > 100) return "Discount percentage cannot exceed 100%.";
      } else {
        if (this.discountInput < 0) return "Discount amount cannot be negative.";
        if (this.discountInput > this.effectiveBillBalance) {
          return `Discount cannot exceed the outstanding balance of ${this.formatCurrency(this.effectiveBillBalance)}.`;
        }
      }
      return null;
    },
  },
  watch: {
    localBillDetails: {
      handler() {
        if (this.isInsuranceScheme) {
          this.setInsurancePaymentOption();
        } else {
          this.setStartingBalance(this.billDetails.balance);
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    // ── Discount helpers ──────────────────────────────────────────────

    setDiscountType(type) {
      this.discountType = type;
      this.discountInput = 0;
      // Reset balances to reflect removed discount
      this.resetPaymentBalances();
    },

    onDiscountInputChange() {
      // Whenever the discount changes, recalculate the first payment row's balance
      this.resetPaymentBalances();
    },

    /**
     * Recalculates the opening balance for the payment rows to reflect the
     * current discount. Only updates the first editable row's balance display
     * (subsequent rows track from there via updateBalance).
     */
    resetPaymentBalances() {
      const editableRows = this.paymentDetailsList.filter((p) => p.editable);
      if (editableRows.length > 0) {
        // The first editable row always starts with the full effective payable amount
        editableRows[0].balance = this.effectivePayableAmount;
      }
    },

    // ── Existing helpers ──────────────────────────────────────────────

    handleBillDetailsInit() {
      this.setStartingBalance(this.billDetails.balance);
      if (this.isInsuranceScheme) {
        this.setInsurancePaymentOption();
      }
    },
    setInsurancePaymentOption() {
      this.paymentDetailsList = [];
      if (this.copayAmount > 0) {
        this.paymentDetailsList.push({
          payment_method_id: this.getInsurancePaymentOption(),
          transaction_code: "",
          selected: true,
          amount_paid: this.billDetails.total_amount - this.copayAmount,
          balance: this.copayAmount,
          editable: false,
        });
      } else {
        this.paymentDetailsList.push({
          payment_method_id: this.getInsurancePaymentOption(),
          transaction_code: "",
          selected: true,
          amount_paid: this.billDetails.total_amount,
          balance: 0,
          editable: false,
        });
      }
    },
    getInsurancePaymentOption() {
      return this.paymentMethods.find((payment) => payment.code === "ST-005");
    },
    setStartingBalance(balance) {
      this.paymentDetailsList[0].balance = parseFloat(balance) || 0;
    },
    checkPaymentMethod(index) {
      const payment = this.paymentDetailsList[index];
      // Clear transaction code for any method other than Mpesa (ST-001).
      // Cash (ST-002) and Card (ST-004) do not require a transaction code.
      if (payment.payment_method_id?.code !== "ST-001") {
        payment.transaction_code = "";
      }
    },
    addPayment() {
      const remainingBalance = this.calculateTotalBalance();
      if (remainingBalance > 0) {
        this.paymentDetailsList.push({
          payment_method_id: null,
          transaction_code: "",
          selected: true,
          amount_paid: 0,
          balance: remainingBalance,
          editable: true,
        });
      } else {
        this.$toast.error("No remaining balance to allocate.");
      }
    },
    addCopayRow() {
      const remainingBalance = this.calculateTotalBalance();
      if (remainingBalance > 0) {
        this.paymentDetailsList.push({
          payment_method_id: null,
          transaction_code: "",
          selected: true,
          amount_paid: 0,
          balance: remainingBalance,
          editable: true,
        });
      } else {
        this.$toast.error("No remaining balance to allocate.");
      }
    },
    updateBalance(index) {
      const totalPaid = this.paymentDetailsList.reduce(
        (sum, payment) => sum + Number(payment.amount_paid || 0),
        0
      );
      // Use effectivePayableAmount (post-discount) as the ceiling
      let remainingBalance = this.effectivePayableAmount - totalPaid;
      this.paymentDetailsList.forEach((payment, i) => {
        payment.balance = i === index ? remainingBalance : payment.balance;
      });
    },
    calculateTotalBalance() {
      const totalPaid = this.paymentDetailsList.reduce(
        (sum, payment) => sum + Number(payment.amount_paid || 0),
        0
      );
      // Remaining is against the post-discount payable amount
      return this.effectivePayableAmount - totalPaid;
    },
    showConfirmModal() {
      if (this.discountValidationError) {
        this.$toast.error(this.discountValidationError);
        return;
      }
      if (this.paymentDetailsList.some((p) => p.editable && !p.payment_method_id)) {
        this.$toast.error("Please select a payment method for all entries.");
        return;
      }
      if (
        this.paymentDetailsList.some(
          (p) =>
            p.editable &&
            p.payment_method_id?.code === "ST-001" &&
            !p.transaction_code
        )
      ) {
        this.$toast.error("Transaction code is required for Mpesa payments.");
        return;
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },

    async confirmPaymentTransactions() {
      try {
        this.isLoading = true;

        // 1. Logic from HEAD: Check if any payment is insurance
        const hasInsurancePayment = this.paymentDetailsList.some(p =>
          p.selected && p.payment_method_id?.code === 'ST-005'
        );

        if (hasInsurancePayment) {
          // Create insurance claim first
          const claimSuccess = await this.createInsuranceClaim();
          if (!claimSuccess) return;
        }

        // 2. Filter editable payments, excluding insurance (from HEAD logic)
        const editablePayments = this.paymentDetailsList.filter(p =>
          p.selected && p.editable && p.payment_method_id?.code !== 'ST-005'
        );

        if (editablePayments.length > 0) {
          // 3. Construct payload combining Discount info (from branch) and filtered payments
          const payload = {
            bill_id: this.billDetails.id,
            // Discount fields retained from the incoming change
            discount_amount: parseFloat(this.computedDiscountAmount) || 0,
            discount_type: this.discountType,        // 'percentage' | 'fixed'
            discount_input_value: this.discountInput, // for audit trail

            // Map the filtered editable payments
            // Backend expects payment_method_id as { id: <value> } not just the id
            payment_transactions: editablePayments.map(p => ({
              payment_method_id: { id: p.payment_method_id.id || p.payment_method_id },
              transaction_code: p.transaction_code,
              amount_paid: parseFloat(p.amount_paid) || 0,
              selected: p.selected,
              balance: parseFloat(p.balance) || 0,
              editable: p.editable,
            })),
          };

          const response = await apiClient.savePaymentTransaction(payload);

          if (response.success) {
            this.$toast.success("Payment transactions saved successfully.");

            // Check for MTIBA reservation confirmation success
           if (response.data?.mtiba_reservation_status?.success) {
             this.$toast.success(response.data.mtiba_reservation_status.message);
           }

           // Check for SHA claim dispatch success
           if (response.data?.sha_claim_status?.success) {
             this.$toast.success(response.data.sha_claim_status.message);
           }
          } else {
            this.$toast.error("Unable to save payment transactions.");
            return;
          }
        }

        // Finalize UI state
        this.$emit("refetch");
        this.closeModal();
        this.$emit("navigateToReceiptInfo");

      } catch (error) {
        this.$toast.error(
          "Unable to save payment transactions, please check your internet connection."
        );
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    resetForm() {
      this.isCoveringFullAmount = false;
      this.discountInput = 0;
      this.discountType = "percentage";
      this.paymentDetails = {
        totalAmount: 0,
        paymentMethod: "",
      };
      this.paymentDetailsList = [
        {
          payment_method_id: null,
          transaction_code: "",
          selected: true,
          amount_paid: 0,
          balance: parseFloat(this.billDetails.balance) || 0,
          editable: true,
        },
      ];
    },
    async createInsuranceClaim() {
      try {
        // Get insurance payment details
        const insurancePayment = this.paymentDetailsList.find(p =>
          p.selected && p.payment_method_id?.code === 'ST-005'
        );

        if (!insurancePayment) {
          this.$toast.error("No insurance payment found.");
          return;
        }

        // Build comprehensive claim data
        const claimData = {
          bill_id: this.billDetails.id,
          visit_id: this.billDetails.visit_id,
          patient_id: this.billDetails.patient.PatientId,
          facility_id: this.billDetails.patient.FacilityId,

          // Patient details
          patient_details: {
            patient_id: this.billDetails.patient.PatientId,
            full_name: this.billDetails.patient.FullName,
            id_number: this.billDetails.patient.IdNumber,
            dob: this.billDetails.patient.Dob,
            gender: this.billDetails.patient.Gender,
            phone_number: this.billDetails.patient.PhoneNumber,
            residence: this.billDetails.patient.Residence,
            county: this.billDetails.patient.County
          },

          // Insurance details
          insurance_details: {
            insurance_id: this.billDetails.patient.patient_insurance_info?.InsuranceId,
            provider_name: this.billDetails.patient.patient_insurance_info?.insurance?.ProviderName,
            member_number: this.billDetails.patient.patient_insurance_info?.MemberNumber,
            relationship: this.billDetails.patient.patient_insurance_info?.RelationshipToPatient
          },

          // Services rendered
          services_rendered: this.billDetails.bill_items.map(item => ({
            bill_item_id: item.id,
            facility_service_id: item.facility_service_id,
            service_name: item.facility_service?.name,
            quantity: item.quantity,
            unit_price: item.unit_price,
            total_amount: item.total_amount,
            amount_paid: item.amount_paid,
            balance: item.balance,
            billing_category: item.billing_category,
            status: item.status?.name
          })),

          // Bill summary
          bill_summary: {
            total_amount: this.billDetails.total_amount,
            amount_paid: this.billDetails.amount_paid,
            balance: this.billDetails.balance,
            sub_totals: this.billDetails.sub_totals,
            bill_number: this.billDetails.bill_number,
            bill_date: this.billDetails.created_at
          },

          // Claim details
          claim_details: {
            claim_type: 'insurance_payment',
            claimed_amount: parseFloat(insurancePayment.amount_paid) || 0,
            claim_status: 'pending_submission',
            claimed_at: new Date().toISOString(),
            claimed_by: this.$store.state.auth.user?.UserId
          },

          // Visit information
          visit_info: {
            visit_id: this.billDetails.visit_id,
            visit_code: this.billDetails.visit?.VisitCode,
            visit_type: this.billDetails.visit?.VisitType,
            visit_date: this.billDetails.visit?.VisitStartTime
          }
        };

        const response = await apiClient.createInsuranceClaim(claimData);

        if (response.success) {
          this.$toast.success("Insurance claim created successfully.");

          if (response.data?.sha_visit_number) {
            this.shaVisitNumber = response.data.sha_visit_number;
            this.shaClaimStatus = response.data.sha_claim_status || 'pending';
            try {
              const previewResponse = await apiClient.previewShaClaim(response.data.sha_visit_number);
              if (previewResponse.success) {
                this.shaClaimPreview = previewResponse.data;
                this.$toast.info("SHA claim preview loaded.");
              }
            } catch (previewError) {
              console.error("SHA claim preview error:", previewError);
            }
          }

          return true;
        } else {
          this.$toast.error("Unable to create insurance claim.");
          return false;
        }
      } catch (error) {
        this.$toast.error("Unable to create insurance claim, please check your internet connection.");
        console.error("Insurance claim creation error:", error);
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "KES",
      }).format(parseFloat(value) || 0);
    },
  },
};
</script>